/**
 * Auth-v2 web token store — RAM-only access token + signup UX
 * state, HttpOnly cookie refresh token.
 *
 * Threat model:
 *  - **Access token** never touches `localStorage`. It lives in a
 *    module-level variable, so XSS can only steal an access token
 *    valid for its remaining life (≤ 15 min) and cannot re-issue
 *    new ones (that requires the refresh token).
 *  - **Refresh token** is a `httpOnly; Secure; SameSite=None` (prod/staging)
 *    or `Lax` (dev) cookie named `hero_rt`. JS cannot read it — set by
 *    the backend on `/auth/sign-in` / `/auth/refresh`.
 *  - **Temp token** and **phone-OTP response** are RAM-only too.
 *    They are mid-flow signup UX state and by design must NOT
 *    persist across a hard reload — refreshing mid-OTP or mid-
 *    signup-wizard should bounce the user back to the Register
 *    screen, forcing them to start the phone-verification loop
 *    over. This is enforced by:
 *    (a) storing both values in module-level variables (this
 *        file) so a page reload naturally drops them, and
 *    (b) the wizard / OTP hooks redirecting to `auth_register`
 *        when their state comes back empty on mount.
 *
 * A tiny `localStorage` sentinel (`hero.hasSession`) keeps the "I
 * think I'm logged in" bit around a hard reload — the actual truth
 * comes from the first `/auth/refresh` call, which succeeds only
 * when the HttpOnly cookie is still valid.
 *
 * See `apps/backend/src/app/auth/README.md`.
 */

import { scheduleAccessTokenRefresh, clearScheduledAccessTokenRefresh } from './scheduleAccessTokenRefresh';

const SESSION_SENTINEL = 'hero.hasSession';

export type PhoneResponse = {
  /** Server returns `true` when the phone has no `User` record yet. */
  isNewUser: boolean;
  otpId: string;
  phone: string;
};

let accessTokenInMemory: string | null = null;
type AccessTokenChangeListener = (token: string | null) => void;
const listeners = new Set<AccessTokenChangeListener>();

function notify() {
  for (const cb of listeners) {
    try {
      cb(accessTokenInMemory);
    } catch (err) {
      // Never let a subscriber crash break the token store.
      console.error('[tokens] listener threw', err);
    }
  }
}

/** RAM-only access token accessor. Never persisted to disk. */
export function getAccessToken(): string | null {
  return accessTokenInMemory;
}

/** Set the current access token. Passing `null` clears it. */
export function setAccessToken(token: string | null): void {
  accessTokenInMemory = token && token.length > 0 ? token : null;
  if (accessTokenInMemory) {
    try {
      localStorage.setItem(SESSION_SENTINEL, '1');
    } catch {
      // Storage may be unavailable (Safari private mode) — the app
      // still works, we just lose the "resume session on reload"
      // hint.
    }
  } else {
    try {
      localStorage.removeItem(SESSION_SENTINEL);
    } catch {
      // ignore
    }
    clearScheduledAccessTokenRefresh();
  }
  scheduleAccessTokenRefresh(accessTokenInMemory);
  notify();
}

/** Subscribe to access-token changes (e.g. to trigger router refresh). */
export function subscribeToAccessToken(listener: AccessTokenChangeListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * True when we have an access token in memory. Different from
 * `hasResumableSession()` — the sentinel might survive a page
 * reload while `accessTokenInMemory` is null (that's the case that
 * triggers the initial refresh call).
 */
export function isLoggedIn(): boolean {
  return accessTokenInMemory !== null;
}

/**
 * True when localStorage carries the "we had a session here before"
 * sentinel. Callers use this on app boot to decide whether to fire
 * a silent `/auth/refresh` (cookie may still be valid) before
 * showing the login screen.
 */
export function hasResumableSession(): boolean {
  try {
    return localStorage.getItem(SESSION_SENTINEL) === '1';
  } catch {
    return false;
  }
}

// ─── Refresh token (HttpOnly cookie) ───────────────────────────────

/**
 * @deprecated Auth-v2: refresh tokens live in HttpOnly cookies on
 * web and cannot be read by JS. This shim always returns `null` so
 * legacy call sites compile without breaking during migration.
 */
export function getRefreshToken(): null {
  return null;
}

/**
 * @deprecated Auth-v2: JS cannot set the refresh cookie. The backend
 * sets `hero_rt` on `/auth/sign-in` / `/auth/refresh`. Kept as a
 * no-op so legacy call sites compile.
 */
export function setRefreshToken(_token: string): void {
  // no-op
}

// ─── Temp token (RAM-only, tab-lifecycle) ──────────────────────────
//
// The temp JWT issued by `auth.verifyOtp` for brand-new users is
// intentionally RAM-only. Hard reload during the signup wizard
// discards it, which forces the wizard's `useSignUpWizardManager`
// to bounce the user back to the Register screen (see that hook's
// "no tempToken" guard). Same treatment as the access token — no
// persistent storage.

let tempTokenInMemory: string | null = null;

export function getTempToken(): string | null {
  return tempTokenInMemory;
}

export function setTempToken(token: string): void {
  tempTokenInMemory = token && token.length > 0 ? token : null;
}

export function clearTempToken(): void {
  tempTokenInMemory = null;
}

// ─── Phone OTP response (RAM-only, tab-lifecycle) ──────────────────
//
// The `{ isNewUser, otpId, phone }` triple handed off from the
// Register screen to the Verify-OTP screen is RAM-only. Hard
// reload during OTP discards it, which forces `useOtpVerify` /
// `useOtpVerifyManager` to bounce the user back to Register (see
// those hooks' `phoneData` guard).

let phoneResponseInMemory: PhoneResponse | null = null;

export function getPhoneResponse(): PhoneResponse[] {
  return phoneResponseInMemory ? [phoneResponseInMemory] : [];
}

export function setPhoneResponse(data: PhoneResponse): void {
  phoneResponseInMemory = data;
}

export function clearPhoneResponse(): void {
  phoneResponseInMemory = null;
}

// ─── High-level flow helpers ───────────────────────────────────────

/**
 * Persists tokens from any API response that returns TokenData.
 * Only the access token is stored — the refresh token, when
 * present in the JSON body, is ignored (backend sets the HttpOnly
 * cookie on the same response for the web transport).
 */
export function saveTokens(data: { access_token?: string; refresh_token?: string }): void {
  if (data.access_token) setAccessToken(data.access_token);
}

/**
 * Persist the OTP-verify response. New users get a `tempToken`
 * that the wizard sends with `signUp`; existing users get a real
 * `accessToken` (and, in cookie transport, a refresh cookie is
 * already set).
 */
export function saveOtpResponse(data: {
  isNewUser: boolean;
  access_token: string;
  refresh_token?: string;
}): void {
  if (data.isNewUser) {
    setTempToken(data.access_token);
    clearPhoneResponse();
  } else {
    saveTokens(data);
    clearPhoneResponse();
  }
}

/**
 * Persist the signup response: real tokens replace the temp token.
 */
export function saveSignUpResponse(data: { access_token?: string; refresh_token?: string }): void {
  clearTempToken();
  saveTokens(data);
}

/**
 * Wipe **session-only** auth artifacts — access token + the
 * `hero.hasSession` sentinel. Deliberately leaves the signup
 * wizard state (`tempToken`, `phoneResponse`) intact.
 *
 * Used on failed refresh (silent boot / SDK 401 interceptor): the
 * user's *session* is dead, but they may be in the middle of a
 * fresh signup / OTP-verify. Nuking their `phoneResponse` at that
 * moment would race with the OTP screen and produce "Missing
 * required data" between mount and submit.
 */
export function clearSessionTokens(): void {
  setAccessToken(null);
  try {
    localStorage.removeItem(SESSION_SENTINEL);
  } catch {
    // ignore
  }
}

/**
 * Wipe every locally-cached auth artifact — session + signup
 * wizard state. Use for **explicit** user logout only; for
 * silent boot-refresh failure use {@link clearSessionTokens}.
 *
 * The HttpOnly refresh cookie is cleared server-side via
 * `POST /auth/sign-out` — this helper just handles the local
 * artifacts.
 */
export function clearAuthTokens(): void {
  clearSessionTokens();
  clearTempToken();
  clearPhoneResponse();
}
